<script setup lang="ts">
import {
  QuestionDisplayType,
  type QuestionMultiChoice,
  QuestionnaireType as QuestionnaireDisplayType,
} from '@/ts/types/dto/interventions-dto';
import {
  computed,
  onBeforeMount,
  watch,
} from 'vue';
import {
  type QuestionAnswerMultiChoiceBody,
  QuestionnaireType,
} from '@/ts/types/dto/session-request-dto';
import ErrorBox from '@/vue/molecules/error-box.vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import QuestionSlider from '@/vue/molecules/components/question/question-slider.vue';
import QuestionCheckbox from '@/vue/molecules/components/question/question-checkbox.vue';
import QuestionGridSelect from '@/vue/molecules/components/question/question-grid-select.vue';
import QuestionImage from '@/vue/molecules/components/question/question-image.vue';
import QuestionGridSelectWithInput from '@/vue/molecules/components/question/question-grid-select-with-input.vue';
import QuestionDropdown from '@/vue/molecules/components/question/question-dropdown.vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import WeightLossCalculationDisplay from '@/vue/molecules/components/question/weight-loss-calculation-display.vue';

const props = defineProps<{
  question: QuestionMultiChoice;
  displayListWithQuestionInRow?: boolean;
  questionnaireType?: string;
}>();

defineEmits<{
  readyToGoNext: [],
}>();

const model = defineModel<QuestionAnswerMultiChoiceBody | null>();
const logger = useLogger();

const value = computed<UuidDTO[]>({
  get() {
    return model.value?.multiChoice ?? [];
  },
  set(newValue) {
    model.value = {
      type: QuestionnaireType.MULTICHOICE,
      multiChoice: newValue,
      customAnswer: props.question.displayType === QuestionDisplayType.CHECKBOX_WITH_INPUT
        ? (model.value?.customAnswer ?? '')
        : model.value?.customAnswer,
    };
  },
});

// Safe custom answer binding
const customAnswerValue = computed({
  get: () => model.value?.customAnswer ?? '',
  set: (newValue: string) => {
    if (model.value) {
      model.value.customAnswer = newValue;
    }
  },
});

onBeforeMount(() => {
  logger.debug(`onBeforeMount ${props.question.contentId}`)();
  if (model.value && value.value.length > 0) {
    logger.trace(`Value of ${props.question.contentId} already set`)();
    // Force reactive trigger for iOS Safari — without this, the component
    // renders as empty comment nodes when remounted with existing data
    // (e.g., navigating back to a previously answered question in a dialog)
    model.value = {...model.value};
  } else if (props.question.multiChoiceSelectedIds && props.question.multiChoiceSelectedIds.length > 0) {
    value.value = props.question.multiChoiceSelectedIds;
  } else if (!model.value) {
    // Initialize the model properly
    model.value = {
      type: QuestionnaireType.MULTICHOICE,
      multiChoice: [],
      ...(props.question.displayType === QuestionDisplayType.CHECKBOX_WITH_INPUT ? {customAnswer: ''} : {}),
    };
  }
});

watch(() => props.question.contentId, () => {
  logger.debug(`watch ${props.question.contentId}`)();
  // emit value to the parents

  if (!model.value?.multiChoice) {
      model.value = {
        type: QuestionnaireType.MULTICHOICE,
        multiChoice: props.question.multiChoiceSelectedIds || [],
        ...(props.question.displayType === QuestionDisplayType.CHECKBOX_WITH_INPUT ? {customAnswer: ''} : {}),
      };
  }
});

const isWeightGoalQuestionnaire = computed(() => {
  return props.questionnaireType === QuestionnaireDisplayType.WEIGHT_LOSS_PERCENT;
});

const selectedPercentage = computed(() => {
  if (!value.value.length || !props.question.multiChoice?.options) {
    return null;
  }
  const selectedOption = props.question.multiChoice.options.find(
    option => option.id === value.value[0],
  );
  return selectedOption?.value ?? null;
});


</script>
<template>
  <div>
    <question-grid-select
      v-if="question.displayType === QuestionDisplayType.ORDERED_LIST"
      v-model="value"
      :theme="displayListWithQuestionInRow ? 'list-question-in-row' : 'list-question-in-col'"
      :question="question"
      @ready-to-go-next="$emit('readyToGoNext')"
    />
    <question-slider
      v-else-if="question.displayType === QuestionDisplayType.HORIZONTAL_SLIDER"
      v-model="value"
      :question="question"
    />
    <question-checkbox
      v-else-if="question.displayType === QuestionDisplayType.CHECKBOX"
      v-model="value"
      :question="question"
    />
    <question-grid-select
      v-else-if="question.displayType === QuestionDisplayType.GRID"
      v-model="value"
      theme="chips"
      :question="question"
    />
    <question-image
      v-else-if="question.displayType === QuestionDisplayType.IMAGE_ANSWERS"
      v-model="value"
      :theme="'list-question-in-row'"
      :question="question"
      @ready-to-go-next="$emit('readyToGoNext')"
    />
    <question-grid-select-with-input
      v-else-if="question.displayType === QuestionDisplayType.CHECKBOX_WITH_INPUT"
      v-model="value"
      v-model:custom-answer="customAnswerValue"
      :theme="displayListWithQuestionInRow ? 'list-question-in-row' : 'list-question-in-col'"
      :question="question"
      @ready-to-go-next="$emit('readyToGoNext')"
    />
    <question-dropdown
      v-else-if="question.displayType === QuestionDisplayType.DROPDOWN"
      v-model="value"
      :question="question"
      @ready-to-go-next="$emit('readyToGoNext')"
    />
    <error-box v-else/>
    <weight-loss-calculation-display
      v-if="isWeightGoalQuestionnaire"
      :selected-percentage="selectedPercentage"
    />
  </div>
</template>

